import type { SVGProps } from 'react';

/**
 * Hand-rolled icon set. Every glyph is a 24x24 stroke icon drawn with
 * `currentColor` and a 1.5px stroke - the same weight the Figma library uses,
 * so no icon dependency is needed.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 16, children, ...props }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const BellIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </Icon>
);

export const SearchIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Icon>
);

export const FilterIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 7h10" />
    <path d="M18 7h2" />
    <path d="M4 17h4" />
    <path d="M12 17h8" />
    <path d="M16 5v4" />
    <path d="M10 15v4" />
  </Icon>
);

export const PlusIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Icon>
);

export const MoreVerticalIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" />
  </Icon>
);

export const InfoIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </Icon>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m14 6-6 6 6 6" />
  </Icon>
);

export const ChevronRightIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m10 6 6 6-6 6" />
  </Icon>
);

export const ChevronDownIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m6 10 6 6 6-6" />
  </Icon>
);

export const CheckIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m5 13 4.5 4.5L19 7" />
  </Icon>
);

export const CheckCircleIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.5 2.5 2.5 4.5-5" />
  </Icon>
);

export const AlertIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <path d="M12 16h.01" />
  </Icon>
);

export const CloseIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 6l12 12" />
    <path d="M18 6 6 18" />
  </Icon>
);

export const EyeIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

export const EditIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 20h4l10.5-10.5a2.12 2.12 0 0 0-3-3L5 17v3Z" />
    <path d="m14.5 6.5 3 3" />
  </Icon>
);

export const TrashIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 7h16" />
    <path d="M9 7V5h6v2" />
    <path d="M6 7l1 13h10l1-13" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </Icon>
);

export const UploadCloudIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M16.5 17.5h1a4 4 0 0 0 .4-7.98A5.5 5.5 0 0 0 7.1 10.6A3.5 3.5 0 0 0 7.5 17.5h1" />
    <path d="M12 12v8" />
    <path d="m9 15 3-3 3 3" />
  </Icon>
);

export const FileTextIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7l-4-4Z" />
    <path d="M14 3v4h4" />
    <path d="M9 13h6" />
    <path d="M9 17h4" />
  </Icon>
);

export const CalendarIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
  </Icon>
);

/* ---------------------------------------------------------------------------
 * Navigation icons
 *
 * These two are lifted verbatim from the Figma "SideNavBar" component so the
 * sidebar glyphs match the design exactly rather than merely looking similar.
 * ------------------------------------------------------------------------- */

export const NavRfpIcon = ({ size = 18, ...props }: IconProps) => (
  <svg
    width={size * (15.5 / 19.5)}
    height={size}
    viewBox="44.25 106.25 15.5 19.5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M58.414 110.414L55.586 107.586C55.211 107.211 54.702 107 54.172 107H47C45.895 107 45 107.895 45 109V123C45 124.105 45.895 125 47 125H57C58.105 125 59 124.105 59 123V111.828C59 111.298 58.789 110.789 58.414 110.414V110.414Z"
    />
    <path d="M59 112H55C54.448 112 54 111.552 54 111V107" />
    <path d="M48.5001 120.966C48.5001 120.963 48.4981 120.961 48.4951 120.961C48.4921 120.961 48.4901 120.963 48.4901 120.966C48.4901 120.969 48.4921 120.971 48.4951 120.971C48.4981 120.971 48.5001 120.969 48.5001 120.966" />
    <path d="M48 114.995H56" />
    <path d="M48 117.995H56" />
    <path d="M55.5001 121C55.5001 120.997 55.4981 120.995 55.4951 120.995C55.4921 120.995 55.4901 120.997 55.4901 121C55.4901 121.003 55.4921 121.005 55.4951 121.005C55.4981 121.005 55.5001 121.003 55.5001 121" />
    <path d="M52.005 120.99C52.005 120.987 52.003 120.985 52 120.985C51.997 120.985 51.995 120.987 51.995 120.99C51.995 120.993 51.997 120.995 52 120.995C52.003 120.995 52.005 120.993 52.005 120.99" />
  </svg>
);

export const NavVendorIcon = ({ size = 18, ...props }: IconProps) => (
  <svg
    width={size * (23.5 / 15.75)}
    height={size}
    viewBox="40.25 178 23.5 15.75"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path d="M60.7923 183.524C61.5824 184.314 61.5824 185.594 60.7923 186.385C60.0023 187.175 58.7214 187.175 57.9314 186.385C57.1414 185.594 57.1414 184.314 57.9314 183.524C58.7214 182.734 60.0023 182.734 60.7923 183.524" />
    <path d="M54.2024 179.912C55.4189 181.129 55.4189 183.101 54.2024 184.318C52.9859 185.534 51.0136 185.534 49.7971 184.318C48.5806 183.101 48.5806 181.129 49.7971 179.912C51.0136 178.696 52.9859 178.696 54.2024 179.912" />
    <path d="M46.0687 183.524C46.8587 184.314 46.8587 185.594 46.0687 186.385C45.2787 187.175 43.9978 187.175 43.2078 186.385C42.4177 185.594 42.4177 184.314 43.2078 183.524C43.9978 182.734 45.2787 182.734 46.0687 183.524" />
    <path d="M63.0002 193V191.904C63.0002 190.523 61.8812 189.404 60.5002 189.404H59.6992" />
    <path d="M41 193V191.904C41 190.523 42.119 189.404 43.5 189.404H44.301" />
    <path d="M57.3392 193V191.399C57.3392 189.466 55.7722 187.899 53.8392 187.899H50.1602C48.2272 187.899 46.6602 189.466 46.6602 191.399V193" />
  </svg>
);

/* ---------------------------------------------------------------------------
 * Auth / account glyphs
 * ------------------------------------------------------------------------- */

export const MailIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="5.5" width="18" height="13" rx="2" />
    <path d="m4 7 8 5.5L20 7" />
  </Icon>
);

export const LockIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
    <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
  </Icon>
);

export const EyeOffIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 4l16 16" />
    <path d="M9.9 5.9A9.7 9.7 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17.2 17.2 0 0 1-3.6 4.3" />
    <path d="M6.4 8.1A16.6 16.6 0 0 0 2.5 12S6 18.5 12 18.5c1.2 0 2.3-.2 3.3-.6" />
    <path d="M10.1 10.3a2.7 2.7 0 0 0 3.7 3.7" />
  </Icon>
);

export const LogoutIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9.5 4.5H6.5A1.5 1.5 0 0 0 5 6v12a1.5 1.5 0 0 0 1.5 1.5h3" />
    <path d="m16 8 4 4-4 4" />
    <path d="M20 12H9.5" />
  </Icon>
);

